/**
 * usePoliceRisiko — TanStack Query hooks for the Police-Ebene «Risiko & Deckung» tab (P2 Slice 2).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.policies.*; mock mode resolves
 * catalog-derived fixtures so the tab renders standalone (no backend). The server owns every
 * decision (validation, gating, gap/ampel) — the UI only triggers + renders.
 *
 * [Source: apps/web/src/features/entity-detail/hooks/useRisikoprofil.ts] (live/mock + qk pattern)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import {
  branchenFelderFuer,
  deriveDeckungAmpel,
  sollRegelnFuer,
  DECKUNG_STATUS,
} from '@brokly/shared';
import type {
  DeckungItem,
  DeckungPutBody,
  DeckungResponse,
  PoliceRisikoPatchBody,
  PoliceRisikoResponse,
  PolicySchadenverlaufResponse,
  SchadenverlaufRohdatenResponse,
} from '@brokly/shared';

// ── Mock fixtures (mock mode only) ──────────────────────────────────────────────

function mockRiskAttributes(policyId: string): PoliceRisikoResponse {
  // A 21000 Betriebshaftpflicht policy with one empty policy-level set (for a standalone render).
  const code = '21000';
  const felder = branchenFelderFuer(code);
  return {
    policy_id: policyId,
    subject_type: 'firma',
    subject_id: '',
    branchencode: code,
    is_family_head: false,
    leaf_codes: [],
    felder: [...felder],
    objekte: [
      {
        id: 'mock-policy-level',
        insurance_object_ref: null,
        object_label: null,
        attributes: {},
        source: 'manuell',
        filled: 0,
        total: felder.length,
        updated_at: new Date(0).toISOString(),
      },
    ],
    gated_fields_suppressed: [],
  };
}

function mockTargetCoverage(policyId: string): DeckungResponse {
  const code = '21000';
  const items: DeckungItem[] = sollRegelnFuer(code).map((rule) => ({
    rule_key: rule.rule_key,
    branchencode: rule.branchencode,
    label: rule.label,
    pflicht_status: rule.pflicht_status,
    rechtsgrundlage: rule.rechtsgrundlage ?? null,
    ziel_text: rule.ziel_text,
    ziel_default_chf: rule.ziel_default_chf ?? null,
    rule_id: null,
    insurance_object_ref: null,
    target_value: null,
    actual_value: null,
    gap_amount: null,
    gap_percent: null,
    status: DECKUNG_STATUS.UNKNOWN,
    evaluation_source: 'manuell',
    notiz: null,
    selbstbehalt: null,
    prioritaet: null,
    ampel: deriveDeckungAmpel({ status: DECKUNG_STATUS.UNKNOWN, pflicht_status: rule.pflicht_status, gap_percent: null }),
  }));
  return { policy_id: policyId, branchencode: code, items };
}

// ── Queries ──────────────────────────────────────────────────────────────────────

export function usePoliceRisikoAttributes(policyId: string) {
  const live = isLiveData();
  return useQuery<PoliceRisikoResponse>({
    queryKey: qk.policyRiskAttributes(policyId, live ? 'live' : 'mock'),
    queryFn: () =>
      live ? apiClient.policies.getRiskAttributes(policyId) : Promise.resolve(mockRiskAttributes(policyId)),
    ...(live ? {} : { initialData: () => mockRiskAttributes(policyId), staleTime: Infinity }),
  });
}

export function usePoliceTargetCoverage(policyId: string) {
  const live = isLiveData();
  return useQuery<DeckungResponse>({
    queryKey: qk.policyTargetCoverage(policyId, live ? 'live' : 'mock'),
    queryFn: () =>
      live ? apiClient.policies.getTargetCoverage(policyId) : Promise.resolve(mockTargetCoverage(policyId)),
    ...(live ? {} : { initialData: () => mockTargetCoverage(policyId), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function usePoliceRisikoPatch(policyId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (body: PoliceRisikoPatchBody) =>
      live ? apiClient.policies.patchRiskAttributes(policyId, body) : Promise.resolve(mockRiskAttributes(policyId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.policyRiskAttributes(policyId, live ? 'live' : 'mock') });
    },
  });
}

export function usePoliceRiskObjectRemove(policyId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (objectId: string) =>
      live ? apiClient.policies.removeRiskObject(policyId, objectId) : Promise.resolve(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.policyRiskAttributes(policyId, live ? 'live' : 'mock') });
    },
  });
}

export function usePoliceTargetCoveragePut(policyId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: ({ ruleKey, body }: { ruleKey: string; body: DeckungPutBody }) =>
      live
        ? apiClient.policies.putTargetCoverage(policyId, ruleKey, body)
        : Promise.resolve(mockTargetCoverage(policyId).items.find((i) => i.rule_key === ruleKey)!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.policyTargetCoverage(policyId, live ? 'live' : 'mock') });
    },
  });
}

// ── P2 Placement Slice 6 — Schadenverlauf ──────────────────────────────────────

/** A mock current Schadenverlauf entry (the Ü2a demo: 2 Schäden / CHF 34'000 / 18 %). */
function mockClaimsHistory(policyId: string): PolicySchadenverlaufResponse {
  const nowIso = new Date().toISOString();
  return {
    policy_id: policyId,
    current: {
      id: 'mock-claims-current',
      policy_id: policyId,
      placement_id: null,
      placement_number: null,
      retrieval_trigger: 'manuell',
      source: 'fixture',
      source_label: 'Schadenauskunftsdienst (Demo-Fixture)',
      provider_ref: `fixture-${policyId.slice(0, 8)}`,
      retrieved_at: nowIso,
      periode_von: '2021-01-01',
      periode_bis: '2026-01-01',
      ergebnis: 'daten',
      schadenanzahl_5j: 2,
      schadensumme_5j: 34000,
      schadenquote_rendement: 18,
      has_rohdaten: true,
      rohdaten_ref: null,
      status: 'aktuell',
      is_current: true,
      superseded_at: null,
      requested_by: null,
      requested_by_name: null,
      created_at: nowIso,
    },
    verlauf: [],
    provider: { driver: 'fixture', configured: true, is_demo: true },
  };
}

export function usePoliceClaimsHistory(policyId: string) {
  const live = isLiveData();
  return useQuery<PolicySchadenverlaufResponse>({
    queryKey: qk.policyClaimsHistory(policyId, live ? 'live' : 'mock'),
    queryFn: () =>
      live ? apiClient.policies.getClaimsHistory(policyId) : Promise.resolve(mockClaimsHistory(policyId)),
    ...(live ? {} : { initialData: () => mockClaimsHistory(policyId), staleTime: Infinity }),
  });
}

export function useRetrieveClaimsHistory(policyId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: () =>
      live ? apiClient.policies.retrieveClaimsHistory(policyId) : Promise.resolve(mockClaimsHistory(policyId).current!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.policyClaimsHistory(policyId, live ? 'live' : 'mock') });
    },
  });
}

export function usePoliceClaimsRohdaten(policyId: string, entryId: string | null) {
  const live = isLiveData();
  return useQuery<SchadenverlaufRohdatenResponse>({
    queryKey: [...qk.policyClaimsHistory(policyId, live ? 'live' : 'mock'), 'rohdaten', entryId ?? ''],
    enabled: entryId !== null,
    queryFn: () =>
      live
        ? apiClient.policies.getClaimsHistoryRohdaten(policyId, entryId!)
        : Promise.resolve({ id: entryId!, rohdaten_json: { provider: 'fixture', hinweis: 'Demo-Rohdaten' }, rohdaten_ref: null }),
  });
}

/** PATCH the policy's igb2b_code (reclassification; invalidates both the policy + the risk tab). */
export function usePolicyReclassify(policyId: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (igb2bCode: string) =>
      live ? apiClient.policies.update(policyId, { igb2b_code: igb2bCode }) : Promise.resolve(null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.policy(policyId) });
      void qc.invalidateQueries({ queryKey: qk.policyRiskAttributes(policyId, live ? 'live' : 'mock') });
      void qc.invalidateQueries({ queryKey: qk.policyTargetCoverage(policyId, live ? 'live' : 'mock') });
    },
  });
}
